import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectSortedProjects, PROJECT_STATUSES } from '../../store/projectsSlice';

// Uniform category styling for all icons
const CATEGORY_STYLE = {
    color: '#6b7280', // Gray
    textColor: '#ffffff'
};

function ProjectsHomepage() {
    const navigate = useNavigate();
    const projects = useSelector(selectSortedProjects);

    // Group projects by icon and then by status within each icon group
    const categorizedProjects = useMemo(() => {
        const categories = {};
        
        projects.forEach(project => {
            const icon = project.icon || 'folder';
            
            if (!categories[icon]) {
                categories[icon] = {
                    ...CATEGORY_STYLE,
                    icon,
                    projects: {
                        'IN_PROGRESS': [],
                        'IN_REVIEW': [],
                        'READY_FOR_GOLIVE': [],
                        'NOT_STARTED': []
                    }
                };
            }
            
            // Group by status, excluding completed projects from homepage
            if (project.status !== 'COMPLETED') {
                const status = project.status || 'NOT_STARTED';
                if (categories[icon].projects[status]) {
                    categories[icon].projects[status].push(project);
                }
            }
        });

        // Filter out categories with no projects
        const filteredCategories = Object.values(categories).filter(category => 
            Object.values(category.projects).some(statusProjects => statusProjects.length > 0)
        );

        return filteredCategories;
    }, [projects]);

    const handleProjectClick = (projectId) => {
        navigate(`/projects/${projectId}`);
    };

    const getStatusOrder = () => ['IN_PROGRESS', 'IN_REVIEW', 'READY_FOR_GOLIVE', 'NOT_STARTED'];

    if (categorizedProjects.length === 0) {
        return (
            <div className="space-y-6">
                <h2 className="text-2xl font-semibold mb-4">Projects Overview</h2>
                <div className="flex items-center justify-center h-64 text-[var(--text-muted)] bg-[var(--surface-bg)] rounded-lg border border-[var(--surface-border)]">
                    <div className="text-center">
                        <span className="material-symbols-outlined text-6xl mb-4 block opacity-50">folder_open</span>
                        <p>No active projects found. Create a project from the sidebar to get started.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <h2 className="text-2xl font-semibold mb-6">Projects Overview</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categorizedProjects.map(category => (
                    <div 
                        key={category.icon}
                        className="bg-[var(--surface-bg)] rounded-lg border border-[var(--surface-border)] overflow-hidden shadow-sm"
                    >
                        {/* Category Header */}
                        <div 
                            className="px-4 py-3 flex items-center justify-center"
                            style={{ 
                                backgroundColor: category.color,
                                color: category.textColor
                            }}
                        >
                            <span className="material-symbols-outlined text-2xl">
                                {category.icon}
                            </span>
                        </div>

                        {/* Projects by Status */}
                        <div className="p-4 space-y-4">
                            {getStatusOrder().map(status => {
                                const statusProjects = category.projects[status];
                                if (!statusProjects || statusProjects.length === 0) return null;

                                return (
                                    <div key={status}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <div
                                                className="w-3 h-3 rounded-full"
                                                style={{ 
                                                    backgroundColor: `var(--status-${status.toLowerCase().replace(/_/g, '-')})` 
                                                }}
                                            />
                                            <h4 className="text-sm font-medium text-[var(--text-secondary)]">
                                                {PROJECT_STATUSES[status]?.label || status}
                                            </h4>
                                            <span className="text-xs text-[var(--text-muted)] bg-[var(--surface-bg-lighter)] px-2 py-1 rounded-full">
                                                {statusProjects.length}
                                            </span>
                                        </div>
                                        
                                        <div className="space-y-2 ml-5">
                                            {statusProjects.map(project => (
                                                <button
                                                    key={project.id}
                                                    onClick={() => handleProjectClick(project.id)}
                                                    className="w-full text-left px-3 py-2 text-sm rounded-md 
                                                             bg-[var(--surface-bg-lighter)] hover:bg-[var(--button-hover-bg)]
                                                             text-[var(--text-primary)] hover:text-[var(--text-primary)]
                                                             border border-transparent hover:border-[var(--surface-border)]
                                                             transition-all duration-150 ease-in-out"
                                                    title={`Go to ${project.name}`}
                                                >
                                                    <div className="truncate">
                                                        {project.name}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>

            {/* Footer info */}
            <div className="text-center text-sm text-[var(--text-muted)] mt-8">
                <p>Showing active projects grouped by category and status. Completed projects are hidden from this overview.</p>
            </div>
        </div>
    );
}

export default ProjectsHomepage; 